import React from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  ArrowRight, Footprints, Store, Users, LineChart, TrendingUp, DollarSign,
  Eye, Megaphone, Crosshair, RefreshCw, Gift, Layers, MapPin, Activity,
  BarChart3, Target, Check, AlertCircle
} from "lucide-react";
import { FootTrafficAuditForm } from "../components/FootTrafficAuditForm";
import { CaseStudies } from "../components/CaseStudies";
import { SEO } from "../components/SEO";

const FootTrafficPage = () => {
  const navigate = useNavigate();
  return (
    <div className="pt-20" data-testid="foot-traffic-page">
      <SEO
        title="Foot Traffic Marketing"
        description="Foot traffic marketing that drives real customers. Measure in-store visits, increase walk-ins, and convert local audiences into measurable revenue with SmartReach™."
        path="/foot-traffic-marketing"
      />

      {/* Hero */}
      <section className="section-padding bg-gradient-to-br from-orange-light via-white to-white">
        <div className="max-w-7xl mx-auto text-center">
          <span className="inline-flex items-center gap-2 text-orange-primary font-semibold text-sm mb-4 bg-white px-4 py-2 rounded-full shadow-sm">
            <Footprints size={16} /> Real-World Results
          </span>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-gray-dark mb-6 leading-tight">
            Foot Traffic Marketing That <span className="text-gradient-orange">Drives Real Customers</span>
          </h1>
          <p className="text-lg md:text-xl text-gray-medium mb-8 max-w-3xl mx-auto">
            Stop chasing impressions. Start counting customers walking through your door — and learn
            exactly which ads, channels, and offers brought them.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => navigate("/contact")} className="btn-primary text-lg" data-testid="foot-traffic-cta-top">
              Get More Customers <ArrowRight size={20} className="inline ml-2" />
            </button>
            <button onClick={() => navigate("/contact")} className="btn-secondary text-lg">
              Book a Demo
            </button>
          </div>
        </div>
      </section>

      {/* WHY FOOT TRAFFIC MATTERS */}
      <section className="section-padding bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-start">
            <div className="lg:col-span-2">
              <span className="inline-flex items-center gap-2 text-orange-primary font-semibold text-sm mb-3 bg-orange-light px-3 py-1 rounded-full">
                Why It Matters
              </span>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-dark mb-4">
                Why Foot Traffic <span className="text-gradient-orange">Matters More Than Impressions</span>
              </h2>
            </div>
            <div className="lg:col-span-3 space-y-4">
              <p className="text-lg text-gray-dark leading-relaxed">
                For local businesses, an impression is just a possibility. A visit is real revenue.
                Foot traffic — the number of actual people walking through your door — is the metric
                that pays your rent, fills your tables, and grows your business.
              </p>
              <p className="text-gray-medium leading-relaxed">
                Yet most advertising platforms still optimize for clicks and views, leaving owners
                guessing what's actually working. SmartReach&trade; flips that — every campaign is built,
                measured, and optimized around real visits.
              </p>
              <div className="grid grid-cols-3 gap-3 pt-2">
                {[
                  { stat: "Visits", desc: "Not impressions" },
                  { stat: "Revenue", desc: "Not vanity metrics" },
                  { stat: "Growth", desc: "Measured monthly" }
                ].map((s, idx) => (
                  <div key={idx} className="bg-orange-light rounded-xl p-3 text-center">
                    <p className="font-display font-bold text-orange-primary">{s.stat}</p>
                    <p className="text-xs text-gray-medium mt-1">{s.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW WE DRIVE VISITS */}
      <section className="section-padding bg-gray-light">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-dark mb-4">
              How We <span className="text-gradient-orange">Drive Visits</span>
            </h2>
            <p className="text-gray-medium max-w-2xl mx-auto text-lg">
              Four mechanics, working together, to turn awareness into a walk-in.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Crosshair, title: "Target Nearby Users", desc: "Hyperlocal radius targeting, foot-traffic audiences, and competitor conquesting reach the people who can actually visit." },
              { icon: RefreshCw, title: "Repeated Exposure", desc: "Frequency capping across channels keeps your brand top of mind without burning out the audience." },
              { icon: Gift, title: "Incentive-Based Visits", desc: "Time-limited offers and loyalty point boosts create urgency and reward immediate action." },
              { icon: Layers, title: "Omnichannel Reinforcement", desc: "Radio, mobile, social, and DOOH reinforce each other — making the visit decision easier." }
            ].map((m, idx) => (
              <div key={idx} className="bg-white p-6 rounded-2xl shadow-card border border-gray-100 card-hover" data-testid={`foot-mechanic-${idx}`}>
                <div className="w-12 h-12 rounded-xl bg-orange-light flex items-center justify-center mb-4">
                  <m.icon className="text-orange-primary" size={24} />
                </div>
                <h3 className="font-display font-bold text-lg text-gray-dark mb-2">{m.title}</h3>
                <p className="text-gray-medium text-sm">{m.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MEASUREMENT */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-dark mb-4">
              <span className="text-gradient-orange">Measurement</span> That Tells The Truth
            </h2>
            <p className="text-gray-medium max-w-2xl mx-auto text-lg">
              The reporting other platforms can't give you.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Activity, title: "Visit Tracking", desc: "Anonymized location signals measure how many people visit after seeing your campaigns — and from which channel." },
              { icon: Target, title: "Campaign Attribution", desc: "Ties visits back to specific creatives, audiences, and dayparts so you know what to scale and what to cut." },
              { icon: TrendingUp, title: "Trend Analysis", desc: "See visit lift over time, identify seasonality, and benchmark performance across locations." }
            ].map((item, idx) => (
              <div key={idx} className="bg-gradient-to-br from-orange-light to-white p-6 rounded-2xl border border-orange-primary/10 card-hover">
                <item.icon className="text-orange-primary mb-3" size={36} />
                <h3 className="font-display font-bold text-lg text-gray-dark mb-2">{item.title}</h3>
                <p className="text-gray-medium text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/measurement" className="text-orange-primary font-semibold inline-flex items-center gap-2 hover:gap-3 transition-all">
              Explore measurement & reporting <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* CASE STUDIES */}
      <CaseStudies title="Foot-Traffic Wins From Local Businesses" />

      {/* USE CASES */}
      <section className="section-padding bg-gray-light">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-dark mb-4">
              Use Cases
            </h2>
            <p className="text-gray-medium max-w-2xl mx-auto">
              Industries where foot-traffic marketing makes the biggest difference.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: Store, title: "Restaurants",
                desc: "Fill tables during lunch, dinner, and weekend peaks. Daypart radio + mobile push within delivery radius drives dine-in over delivery.",
                link: "/solutions/restaurants"
              },
              {
                icon: Layers, title: "Retail Stores",
                desc: "Drive footfall during launches, seasonal sales, and slow weeks. Combine DOOH, mobile geofencing, and loyalty boosts.",
                link: "/solutions/retail"
              },
              {
                icon: Megaphone, title: "Events & Entertainment",
                desc: "Sell tickets faster and lift attendance with urgency-driven radio + digital push targeted to local audiences.",
                link: "/solutions/entertainment"
              }
            ].map((u, idx) => (
              <Link key={idx} to={u.link} className="bg-white p-6 rounded-2xl shadow-card card-hover block" data-testid={`foot-use-case-${idx}`}>
                <u.icon className="text-orange-primary mb-3" size={32} />
                <h3 className="font-display font-bold text-lg text-gray-dark mb-2">{u.title}</h3>
                <p className="text-gray-medium text-sm mb-3">{u.desc}</p>
                <span className="text-orange-primary font-semibold text-sm inline-flex items-center gap-1">
                  See solution <ArrowRight size={14} />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-dark mb-4">
              The <span className="text-gradient-orange">Benefits</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: TrendingUp, title: "Increase Walk-Ins", desc: "More real customers, more often. Measurable lift in store visits within 30–90 days." },
              { icon: Target, title: "Improve Conversion", desc: "Customers near your business with clear offers convert at higher rates than broad audiences." },
              { icon: DollarSign, title: "Reduce Wasted Spend", desc: "Outcome-based optimization shifts dollars away from channels that don't drive visits." }
            ].map((b, idx) => (
              <div key={idx} className="bg-gradient-to-br from-orange-light to-white p-8 rounded-2xl border border-orange-primary/10 card-hover">
                <b.icon className="text-orange-primary mb-4" size={40} />
                <h3 className="font-display font-bold text-2xl text-gray-dark mb-3">{b.title}</h3>
                <p className="text-gray-medium">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CAMPAIGN EXAMPLES */}
      <section className="section-padding bg-gray-light">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-dark mb-4">Campaign Examples</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { title: "Grand Opening Push", desc: "Radio + hyperlocal mobile + DOOH within a 5-mile radius for launch week. Loyalty bonuses for first-time visitors." },
              { title: "Weekend Promotions", desc: "Time-based offers Friday-Sunday with double-points loyalty boosts and geofenced mobile reminders." },
              { title: "Compete & Conquest", desc: "Geo-target audiences near competitor locations with differentiated offers to win nearby high-intent shoppers." }
            ].map((c, idx) => (
              <div key={idx} className="bg-white p-6 rounded-2xl shadow-card card-hover">
                <Megaphone className="text-orange-primary mb-3" size={32} />
                <h3 className="font-display font-bold text-lg text-gray-dark mb-2">{c.title}</h3>
                <p className="text-gray-medium text-sm">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FootTrafficAuditForm />

      <section className="section-padding gradient-orange">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-6">
            Turn local audiences into walk-in customers
          </h2>
          <button onClick={() => navigate("/contact")} className="bg-white text-orange-primary px-8 py-4 rounded-full font-semibold text-lg hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
            Get More Customers <ArrowRight size={20} className="inline ml-2" />
          </button>
        </div>
      </section>
    </div>
  );
};

export default FootTrafficPage;
